import React, { useState, useEffect, useContext } from 'react';
import UserContext from '../../../components/User_Context.js';
import * as menu from "../../../assets/menus/menu.js"
import { useParams } from "react-router-dom";
import * as breadcrumb from "../../../components/ui/breadcrumb.js";
import { useTranslation } from 'react-i18next';
import * as api from "../../../api/apiConnector.js";
import waiting from "../../../assets/images/waiting.svg";
import * as utils from "../../../components/utils.js";
import Choices from '../../../components/Choices.js';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';


/**
 * Item Edit Screen Component
 * Handles editing of DMS file metadata including name, expiration date, classifications, categories, and keywords
 */
function Item_Edit(params) {

    // Route parameters - file type and ID
    const { type, id } = useParams();
    const { currentUser } = useContext(UserContext);

    // State management for file data and metadata
    const [item, setItem] = useState(null);                    // Current file item data
    const [metaData, setMetadata] = useState(null);            // File metadata (classifications, categories, keywords)
    const { t } = useTranslation('labels');                    // Translation function

    // Vocabulary states for dropdowns
    const [dmsClassificationVoc, setDmsClassificationVoc] = useState([]);  // Classification vocabulary
    const [dmsCategoriesVoc, setDmsCategoriesVoc] = useState([]);          // Categories vocabulary (DARM only)
    const [dmsKeywordsVoc, setDmsKeywordsVoc] = useState([]);              // Keywords vocabulary (DARM only)
    const [dmsValidationFlowVoc, setDmsValidationFlowVoc] = useState([]);
    // Form state management
    const [toSave, setToSave] = useState(false);               // Flag to enable/disable save button
    const [selectedClassifications, setSelectedClassifications] = useState([]);  // Selected classifications
    const [selectedValidation, setSelectedValidation] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);            // Selected categories
    const [selectedKeywords, setSelectedKeywords] = useState([]);                // Selected keywords
    const [forceRender, setForceRender] = useState(0);         // Counter to force form re-render
    /**
     * Set breadcrumb navigation path
     * Creates navigation breadcrumb with Home > DMS > Current File Name
     */
    const setBreadCrumb = (params) => {
        var path = [{
            link: "/",
            name: "Home"
        },
        {
            link: "/admin/dms",
            name: t('dms', { ns: 'labels' })
        }
        ];

        // Add current file to breadcrumb if editing a file
        if (type == "file") {
            path.push({
                link: `dms/itemEdit/file/${id}`,
                name: item?.file?.Name
            })
        }

        breadcrumb.set(path);
    }

    /**
     * Mark form as having changes to save
     * Enables the save button when form data is modified
     */
    const _toSave = () => {
        setToSave(true);
    }


    /**
     * Main initialization effect
     * Loads file data, metadata, and vocabularies when component mounts
     */
    useEffect(() => {
        (async () => {
            if (type == "file") {
                // Load vocabularies first to ensure they're available when populating selections
                await loadVocabularies();

                // Get file information from API
                var itemResult = await api.sendMessage("dms/getFileInfo", {
                    user: currentUser(),
                    body: {
                        id
                    }
                });

                // Set file item data
                setItem(utils.clone(itemResult));

                // Get file metadata (classifications, categories, keywords)
                var getMeta = await api.sendMessage("dms/getMeta", {
                    user: currentUser(),
                    body: {
                        id: itemResult.file.LastVersion,
                        fileId: itemResult.file.Id
                    },
                });

                setMetadata(getMeta);

                // Set breadcrumb navigation after item is loaded
            }

            // Initialize menu with current user
            menu.write({ user: currentUser() });

        })();
    }, []);

    /**
     * Update breadcrumb when item changes
     * Ensures breadcrumb shows current file name when item data is updated
     */
    useEffect(() => {
        if (item) {
            setBreadCrumb();
        }
    }, [item]);

    /**
     * Populate classifications from metadata
     * Maps classification codes from metadata to full vocabulary objects
     */
    const populateClassifications = async () => {
        if (metaData && metaData.classifications && Array.isArray(metaData.classifications)) {
            // Wait for classification vocabulary to be loaded
            while (dmsClassificationVoc.length === 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Map classification codes to full vocabulary objects
            const initialClassifications = metaData.classifications.map(classificationObj =>
                dmsClassificationVoc.find(voc => voc.Code === classificationObj.ClassificationCode)
            ).filter(Boolean); // Remove undefined values

            setSelectedClassifications(initialClassifications);
            setForceRender(prev => prev + 1); // Force form re-render
        } else {
            setSelectedClassifications([]);
        }
    };

    const populateValidationFlow = async () => {
        if (metaData && metaData.FileStatus) {
            // Wait for categories vocabulary to be loaded
            while (dmsValidationFlowVoc.length === 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Map category codes to full vocabulary objects
            const initialValidation =
                dmsValidationFlowVoc.find(voc => voc.Code === metaData.FileStatus)
                   ; // Remove undefined values

            setSelectedValidation([initialValidation]);
            setForceRender(prev => prev + 1); // Force form re-render
        } else {
            setSelectedCategories([]);
        }
    };

    /**
     * Populate categories from metadata
     * Maps category codes from metadata to full vocabulary objects (DARM profile only)
     */
    const populateCategories = async () => {
        if (metaData && metaData.categories && Array.isArray(metaData.categories)) {
            // Wait for categories vocabulary to be loaded
            while (dmsCategoriesVoc.length === 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Map category codes to full vocabulary objects
            const initialCategories = metaData.categories.map(categoryObj =>
                dmsCategoriesVoc.find(voc => voc.Code === categoryObj.CategoryCode)
            ).filter(Boolean); // Remove undefined values

            setSelectedCategories(initialCategories);
            setForceRender(prev => prev + 1); // Force form re-render
        } else {
            setSelectedCategories([]);
        }
    };

    /**
     * Populate keywords from metadata
     * Maps keyword codes from metadata to full vocabulary objects (DARM profile only)
     */
    const populateKeywords = async () => {
        if (metaData && metaData.keywords && Array.isArray(metaData.keywords)) {
            // Wait for keywords vocabulary to be loaded
            while (dmsKeywordsVoc.length === 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Map keyword codes to full vocabulary objects
            const initialKeywords = metaData.keywords.map(keywordObj =>
                dmsKeywordsVoc.find(voc => voc.Code === keywordObj.KeywordCode)
            ).filter(Boolean); // Remove undefined values

            setSelectedKeywords(initialKeywords);
            setForceRender(prev => prev + 1); // Force form re-render
        } else {
            setSelectedKeywords([]);
        }
    };

    /**
     * useEffect to populate classifications when metadata changes
     * Triggers when metadata is loaded or updated
     */
    useEffect(() => {
        (async () => {
          await  populateClassifications();
          await populateValidationFlow();
          await populateCategories();
          await populateKeywords();
        })();
    }, [metaData]);


    /**
     * Load DMS vocabularies for classification, categories, and keywords
     * Fetches vocabulary data from API to populate dropdown choices
     */
    const loadVocabularies = async () => {
        try {
            // Load classification vocabulary (available for all profiles)
            const classificationResult = await api.sendMessage("admin/vocs/get", {
                user: currentUser(),
                body: { voc_id: "dms_classification_types" }
            });
            if (classificationResult.success) {
                setDmsClassificationVoc(classificationResult.items || []);
            }

            const validationResult = await api.sendMessage("admin/vocs/get", {
                user: currentUser(),
                body: { voc_id: "dms_files_validation_flow" }
            });
            if (validationResult.success) {
                setDmsValidationFlowVoc(validationResult.items || []);
            }

            // Load categories and keywords vocabularies (DARM profile only)
            if (process.env.REACT_APP_PROFILE === "DARM") {
                // Load categories vocabulary
                const categoriesResult = await api.sendMessage("admin/vocs/get", {
                    user: currentUser(),
                    body: { voc_id: "dms_category_types" }
                });
                if (categoriesResult.success) {
                    setDmsCategoriesVoc(categoriesResult.items || []);
                }

                // Load keywords vocabulary
                const keywordsResult = await api.sendMessage("admin/vocs/get", {
                    user: currentUser(),
                    body: { voc_id: "dms_keywords" }
                });
                if (keywordsResult.success) {
                    setDmsKeywordsVoc(keywordsResult.items || []);
                }
            }
        } catch (error) {
            console.error("Error loading vocabularies:", error);
        }
    };

    /**
     * Update item details with form data
     * Saves changes to file name, expiration date, and metadata selections
     */
    const updateItemDetails = async () => {
        try {
            // Validate required fields
            if (!item.file.Name) {
                withReactContent(Swal).fire({
                    title: t('info', { ns: 'labels' }),
                    icon: "info",
                    text: t("fileNameRequired", { ns: "labels" }) || "File name is required",
                    showConfirmButton: true
                });
                return;
            }

            // Prepare update data with current form values
            const updateData = {
                id: id,
                name: item.file.Name,
                expirationDate: item.file.ExpirationDate,
                classifications: selectedClassifications.map(classification => ({
                    ClassificationCode: classification.Code
                })),
                categories: selectedCategories.map(category => ({
                    CategoryCode: category.Code
                })),
                keywords: selectedKeywords.map(keyword => ({
                    KeywordCode: keyword.Code
                })),
                fileStatus: selectedValidation[0].Code
            };

            // Send update request to API
            const result = await api.sendMessage("dms/updateFile", {
                user: currentUser(),
                body: updateData
            });

            if (result.success) {
                // Show success message
                withReactContent(Swal).fire({
                    title: "Success",
                    icon: "success",
                    text: t("itemUpdatedSuccessfully", { ns: "labels" }) || "Item updated successfully",
                    showConfirmButton: true,
                    confirmButtonText: "Close",
                });
                setToSave(false); // Reset save state after successful save
            } else {
                // Show error message
                withReactContent(Swal).fire({
                    title: t('error', { ns: 'labels' }),
                    icon: "error",
                    text: result.error,
                    showConfirmButton: true
                });
            }
        } catch (error) {
            // Show error message for exceptions
            withReactContent(Swal).fire({
                title: t('error', { ns: 'labels' }),
                icon: "error",
                text: t("updateError", { ns: "labels" }) || "Error updating item",
                showConfirmButton: true
            });
        }
    };

    /**
     * Draw classifications Choices component
     */
    const _drawClassificationsChoices = () => {
        return (
            <div className="row pt-3">
                <div className="col-xl">
                    <label className="form-label">
                        {t("classification", { ns: "labels" })}
                    </label>
                    {dmsClassificationVoc.length > 0 ? (
                        <Choices
                            voc={dmsClassificationVoc}
                            value="Code"
                            text="Name"
                            multiple={true}
                            selected={selectedClassifications}
                            onChange={(e) => {
                                setSelectedClassifications(e.selected || []);
                                _toSave();
                            }}
                        />
                    ) : (
                        <div className="text-muted">Loading classifications...</div>
                    )}
                </div>
            </div>
        );
    };


    const _drawValidationFlowChoices = () => {
        return (
            <div className="row pt-3">
                <div className="col-xl">
                    <label className="form-label">
                        {t("status", { ns: "labels" })}
                    </label>
                    {dmsValidationFlowVoc.length > 0 ? (
                        <Choices
                            voc={dmsValidationFlowVoc}
                            value="Code"
                            text="Name"
                            multiple={false}
                            selected={selectedValidation}
                            onChange={(e) => {
                                setSelectedValidation(e.selected || []);
                                _toSave();
                            }}
                        />
                    ) : (
                        <div className="text-muted">Loading classifications...</div>
                    )}
                </div>
            </div>
        );
    };

    /**
     * Draw categories Choices component
     */
    const _drawCategoriesChoices = () => {
        return (
            <div className="row pt-3">
                <div className="col-xl">
                    <label className="form-label">
                        {t("dmsCategory", { ns: "labels" })}
                    </label>
                    {dmsCategoriesVoc.length > 0 ? (
                        <Choices
                            voc={dmsCategoriesVoc}
                            value="Code"
                            text="Name"
                            multiple={true}
                            selected={selectedCategories}
                            onChange={(e) => {
                                setSelectedCategories(e.selected || []);
                                _toSave();
                            }}
                        />
                    ) : (
                        <div className="text-muted">Loading categories...</div>
                    )}
                </div>
            </div>
        );
    };

    /**
     * Draw keywords Choices component
     */
    const _drawKeywordsChoices = () => {
        return (
            <div className="row pt-3">
                <div className="col-xl">
                    <label className="form-label">
                        {t("keywords", { ns: "labels" })}
                    </label>
                    {dmsKeywordsVoc.length > 0 ? (
                        <Choices
                            voc={dmsKeywordsVoc}
                            value="Code"
                            text="Name"
                            multiple={true}
                            selected={selectedKeywords}
                            onChange={(e) => {
                                setSelectedKeywords(e.selected || []);
                                _toSave();
                            }}
                        />
                    ) : (
                        <div className="text-muted">Loading keywords...</div>
                    )}
                </div>
            </div>
        );
    };

    const _drawItemEdit = () => {
        return (
            <div key={forceRender} className="container-fluid">
                <div className="row">
                    <h5 className="mb-0">{t("creator", { ns: "labels" })}</h5>
                    <span>{item?.file?.ItemCreatedBy || t("unknown", { ns: "labels" })}</span>
                </div>
                <div className="row pt-3">
                    <h5 className="mb-0">{t("lastUpdate", { ns: "labels" })}</h5>
                    <span>{item?.file?.ItemLastUpdated ? new Date(item.file.ItemLastUpdated).toLocaleString() : t("unknown", { ns: "labels" })}</span>
                </div>
                <div className="row pt-3">
                    <div className="col-xl">
                        <label htmlFor="itemNameEdit" className="form-label">
                            {t("fileName", { ns: "labels" })}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="itemNameEdit"
                            defaultValue={item?.file?.Name || ""}
                            placeholder={t("name", { ns: "labels" })}
                            onChange={(e) => {
                                const updatedItem = {
                                    ...item,
                                    file: {
                                        ...item.file,
                                        Name: e.target.value
                                    }
                                };
                                setItem(updatedItem);
                                _toSave();
                            }}
                        />
                    </div>
                </div>

                {/* Summary */}
                {metaData?.summary && (
                    <div className="row pt-3">
                        <label htmlFor="summaryEdit" className="form-label">
                            {t("summary", { ns: "labels" })}
                        </label>
                        <div style={{ height: "8em", overflowY: "auto" }}>
                            <div id="summaryEdit" className="col-xl-12">
                                {metaData.summary}
                            </div>
                        </div>
                    </div>
                )}

                {_drawClassificationsChoices()}

               
                    <>
                        {_drawCategoriesChoices()}
                        {_drawKeywordsChoices()}
                    </>
                

                {_drawValidationFlowChoices()}

                <div className="row pt-3">
                    <div className="col-xl">
                        <label htmlFor="expirationDateEdit" className="form-label">
                            {t("expirationDate", { ns: "labels" })}
                        </label>
                        <input
                            type="datetime-local"
                            className="form-control"
                            id="expirationDateEdit"
                            value={item?.file?.ExpirationDate ? new Date(item.file.ExpirationDate).toISOString().slice(0, 16) : ""}
                            onChange={(e) => {
                                const updatedItem = {
                                    ...item,
                                    file: {
                                        ...item.file,
                                        ExpirationDate: e.target.value ? new Date(e.target.value).toISOString() : null
                                    }
                                };
                                setItem(updatedItem);
                                _toSave();
                            }}
                        />
                    </div>
                </div>

                {/* Topics data */}
                {metaData?.topics && (
                    <div className="row pt-3">
                        <label htmlFor="topicDataEdit" className="form-label">
                            {t("topics", { ns: "labels" })}
                        </label>
                        <div style={{ height: "8em", overflowY: "auto" }}>
                            <div id="topicDataEdit" className="col-xl-12">
                                {metaData.topics}
                            </div>
                        </div>
                    </div>
                )}

                {/* <form>
                    <div className="pt-3">
                        <button
                            type="button"
                            className="btn btn-danger light ms-1 me-1"
                            onClick={() => {
                                // Handle cancel action
                                window.history.back();
                            }}
                        >
                            {t("cancel", { ns: "labels" })}
                        </button>
                        <button
                            id="saveItemEdit"
                            type="button"
                            onClick={async () => {
                                await updateItemDetails();
                            }}
                            className="btn btn-primary pl-3"
                            disabled={!toSave}
                        >
                            {t("submit", { ns: "labels" })}
                        </button>
                    </div>
                </form> */}
            </div>
        );
    }

    return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>

            {/* 1) HEADER + TOOLBAR (never scrolls) */}
            <header className="bg-white shadow-sm" style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                <div className="container-fluid d-flex justify-content-between align-items-center py-2 fixed-toolbar">

                    {/* → breadcrumb navigation */}
                    <ol className="breadcrumb mb-0"></ol>

                    {/* → save / delete icons */}
                    <div className="d-flex gap-3">
                        <a
                            className="text-secondary fs-13"
                            onClick={() => window.history.back()}
                            role="button"
                            title="Go back"
                        >
                            <i className="fa-solid fa-arrow-left fa-2x"></i>
                        </a>
                        <a
                            className="text-primary fs-13"
                            onClick={async () => {
                                if (toSave) {
                                    await updateItemDetails();
                                } else {
                                    withReactContent(Swal).fire({
                                        title: t('info', { ns: 'labels' }),
                                        icon: "info",
                                        text: t('nothingToSave', { ns: 'labels' }),
                                        showConfirmButton: true,
                                        allowOutsideClick: false
                                    });
                                }
                            }}
                            role="button"
                            title="Save data"
                        >
                            <i className="fa-solid fa-floppy-disk fa-2x"></i>
                        </a>

                    </div>
                </div>
            </header>

            {/* 2) SCROLLABLE CONTENT AREA*/}
            <main className="flex-grow-1 overflow-auto">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="card">
                                <div className="card-body p-0">
                                    <div
                                        className="tab-content"
                                        id="tabs-content"
                                        style={{ padding: 15 }}
                                    >
                                        {item && metaData ? (
                                            _drawItemEdit()
                                        ) : (
                                            <div className="text-center">
                                                <img src={waiting} alt="Waiting" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export { Item_Edit };


